/**
 * O arquivo de um material da base de conhecimento, do lado da tela.
 *
 * ## Por que num módulo próprio
 *
 * Porque as mesmas três perguntas aparecem em lugares diferentes da página de
 * Vídeos e Manuais -- "isto é um vídeo enviado?", "cabe no limite?", "como eu
 * digo o tamanho?" -- e cada resposta escrita de novo é uma que vai divergir.
 *
 * E porque a primeira delas é a que decide qual CARTÃO desenhar. Errá-la não
 * dá erro nenhum: o vídeo enviado cai no cartão de link, com `href=""`, e
 * clicar nele recarrega a página.
 *
 * ## A lista de extensões é a MESMA do servidor
 *
 * `PoliticaDoArquivoDaBase::TIPOS_DE_VIDEO`. Os dois lados precisam concordar:
 * um a mais aqui faz a tela oferecer um tipo que o servidor recusa depois do
 * envio inteiro; um a menos esconde o cartão certo de um material válido.
 */

/** O que a base aceita como VÍDEO. Igual ao servidor. */
export const EXTENSOES_DE_VIDEO = ['mp4', 'webm', 'mov'];

/** O que a base aceita como DOCUMENTO. Igual ao servidor. */
export const EXTENSOES_DE_DOCUMENTO = ['pdf', 'txt', 'doc', 'docx'];

/** O `accept` de um `<input type="file">`, a partir de uma lista. */
export const aceitar = (extensoes) => extensoes.map((e) => `.${e}`).join(',');

/**
 * A extensão do arquivo de um material, minúscula e sem o ponto.
 *
 * Lê `original_filename` e cai em `file_path` -- os dois carregam a mesma
 * extensão, mas registros antigos podem ter só o segundo.
 */
export const extensaoDoMaterial = (material) => {
    const nome = String(material?.original_filename || material?.file_path || '');
    const ponto = nome.lastIndexOf('.');

    return ponto === -1 ? '' : nome.slice(ponto + 1).toLowerCase();
};

/** O material tem arquivo guardado no servidor? */
export const temArquivo = (material) => Boolean(material?.file_path || material?.original_filename);

/**
 * É um vídeo ENVIADO COMO ARQUIVO (e não um link de YouTube)?
 *
 * É esta resposta que escolhe o cartão. O material com `video_url` abre numa
 * aba; o enviado toca aqui dentro, porque não há para onde abrir.
 */
export const ehVideoEnviado = (material) => (
    temArquivo(material) && EXTENSOES_DE_VIDEO.includes(extensaoDoMaterial(material))
);

/** "18,4 MB" -- o tamanho como a pessoa lê. */
export const tamanhoLegivel = (bytes) => {
    const n = Number(bytes) || 0;

    if (n >= 1048576) {
        return `${(n / 1048576).toFixed(1).replace('.', ',')} MB`;
    }

    return `${Math.max(1, Math.round(n / 1024))} KB`;
};

/**
 * O arquivo escolhido serve? Devolve a mensagem de recusa, ou `null`.
 *
 * ## Por que a tela confere ANTES de enviar
 *
 * Porque sem isso o arquivo sobe INTEIRO para ser recusado no fim. Foi
 * exatamente o defeito que a política de anexos foi criada para corrigir: o
 * front do chat deixava passar até 21 MB e o servidor recusava a partir de 10,
 * então um arquivo de 15 MB viajava inteiro antes de receber um "dados
 * inválidos" que não dizia o motivo. Num vídeo de 300 MB isso são minutos.
 *
 * Quem GARANTE continua sendo o servidor. Isto aqui é para a pessoa saber
 * agora, e não depois.
 *
 * @param {File} arquivo
 * @param {{tipos: string[], limite_mb: number}} limite  o que o servidor disse
 */
export const recusaDoArquivo = (arquivo, limite) => {
    if (!arquivo) {
        return 'Escolha um arquivo.';
    }

    const tipos = Array.isArray(limite?.tipos) ? limite.tipos : [];
    const nome = String(arquivo.name || '');
    const ponto = nome.lastIndexOf('.');
    const extensao = ponto === -1 ? '' : nome.slice(ponto + 1).toLowerCase();

    if (tipos.length > 0 && !tipos.includes(extensao)) {
        return `O arquivo deve ser do tipo: ${tipos.join(', ')}.`;
    }

    /*
     * Sem limite conhecido, NÃO recusa.
     *
     * O número vem de uma chamada ao servidor que pode ter falhado. Tratar a
     * ausência como zero transformaria uma falha de rede numa tela que recusa
     * todo arquivo -- e a pessoa procuraria o defeito no arquivo dela.
     */
    const limiteEmMb = Number(limite?.limite_mb) || 0;

    if (limiteEmMb > 0 && Number(arquivo.size) > limiteEmMb * 1048576) {
        return `O arquivo tem ${tamanhoLegivel(arquivo.size)} e o limite é ${limiteEmMb} MB.`;
    }

    return null;
};
